import { useState, useRef, useMemo, useLayoutEffect, useEffect, useCallback } from 'react';
import { animate, useReducedMotion } from 'framer-motion';
import { ZOOM, diffDays, startOfDay } from './constants';
import { CAMARA_T } from '../utils/motion';

const DIA_MS = 86400000;

// Ventana que cubre las dos escalas: durante el viaje no puede faltar linea de
// tiempo en ninguno de los bordes.
const unirVentanas = (a, b) => {
  const inicio = Math.min(a.firstDay.getTime(), b.firstDay.getTime());
  const fin = Math.max(
    a.firstDay.getTime() + a.totalDays * DIA_MS,
    b.firstDay.getTime() + b.totalDays * DIA_MS
  );
  const firstDay = new Date(inicio);
  return {
    firstDay,
    totalDays: Math.round((fin - inicio) / DIA_MS),
    todayOffset: diffDays(startOfDay(new Date()), firstDay),
  };
};

// Dias que la camara llega a mostrar en algun momento del viaje. El ancho del
// dia cambia siempre en el mismo sentido y el centro va en linea recta, asi que
// los extremos estan en la salida y en la llegada.
const rangoDelViaje = ({ inicioMs, centro0, centro1, dw0, dw1, ancho }) => {
  const dia = (ms) => (ms - inicioMs) / DIA_MS;
  const m0 = ancho / 2 / dw0;
  const m1 = ancho / 2 / dw1;
  const desde = Math.min(dia(centro0) - m0, dia(centro1) - m1);
  const hasta = Math.max(dia(centro0) + m0, dia(centro1) + m1);
  const margen = (hasta - desde) * 0.1 + 2;
  return { desde: desde - margen, hasta: hasta + margen };
};

/**
 * Zoom del Gantt como una camara, con el mismo resorte que el organigrama.
 *
 * Al cambiar de escala el ancho del dia viaja de la escala vieja a la nueva
 * (en escala logaritmica: cada cuadro acerca o aleja en la misma proporcion) y
 * la fecha del centro de la pantalla se queda quieta, o la camara viaja a otra
 * fecha a la vez (Ajustar, Hoy). Las barras se estiran o se juntan en su sitio
 * y los encabezados de las dos escalas se funden uno en otro.
 *
 * Como la camara del organigrama, el viaje no pasa por React: cada cuadro
 * escribe la variable CSS --dw (y --p, el avance, para el fundido) en el
 * lienzo y mueve el scroll. Todo lo que esta sobre la linea de tiempo se ubica
 * con esas variables (ver xDias/anchoDias), asi que el navegador lo recoloca
 * solo. React pinta dos veces por viaje: al salir (ventana de las dos escalas,
 * solo lo que la camara va a mostrar) y al llegar (ventana de la escala nueva,
 * sin que nada se mueva en pantalla).
 *
 * Tambien lleva el scroll horizontal: centrar hoy al abrir y conservar la fecha
 * del borde izquierdo cuando la ventana se recalcula (cambian las tareas).
 *
 * ventanaPara(zoom) -> { firstDay, totalDays, todayOffset } (ventanaGantt).
 * escalas: { [zoom]: { px } } (las del Gantt por defecto; el Roadmap trae las suyas).
 * hoyEn: en que fraccion del ancho queda hoy al abrir y con "Hoy" (centro por defecto).
 */
export const useCamaraGantt = ({
  zoomInicial, ventanaPara, scrollRef, lienzoRef, escalas = ZOOM, hoyEn = 0.5,
}) => {
  const reduce = useReducedMotion();
  const [zoom, setZoom] = useState(zoomInicial);
  // Viaje en curso: { desde, hasta, ventana, rango, dw0 } (fijo de punta a punta).
  const [viaje, setViaje] = useState(null);
  const animacion = useRef(null);
  // Ultimo cuadro pintado del viaje: { p, dw, centroMs }.
  const cuadroActual = useRef(null);
  const pendiente = useRef({ tipo: 'hoy' });
  const bordeIzqMs = useRef(null);

  const ventanaZoom = useMemo(() => ventanaPara(zoom), [ventanaPara, zoom]);
  const ventana = viaje ? viaje.ventana : ventanaZoom;
  const dayWidth = escalas[zoom].px;
  const firstDayMs = ventana.firstDay.getTime();

  // Valores del ultimo render, para leerlos desde los callbacks sin recrearlos.
  const actual = useRef({});
  actual.current = { zoom, viaje, ventana, dayWidth, firstDayMs };

  const pintar = useCallback((dw, p) => {
    const lienzo = lienzoRef.current;
    if (!lienzo) return;
    lienzo.style.setProperty('--dw', String(dw));
    lienzo.style.setProperty('--p', String(p));
  }, [lienzoRef]);

  // Despues de cada render que cambia la ventana o la escala: poner el ancho del
  // dia en el lienzo y acomodar el scroll para que nada salte.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !lienzoRef.current) return;
    let x = null;
    if (viaje) {
      // Salida del viaje: la ventana paso a la de las dos escalas. El primer
      // cuadro queda identico a lo que habia; desde aca manda la animacion.
      const c = cuadroActual.current || { p: 0, dw: viaje.dw0, centroMs: firstDayMs };
      pintar(c.dw, c.p);
      el.scrollLeft = Math.max(0, ((c.centroMs - firstDayMs) / DIA_MS) * c.dw - el.clientWidth / 2);
      return;
    }
    pintar(dayWidth, 1);
    const p = pendiente.current;
    pendiente.current = null;
    if (p?.tipo === 'hoy') {
      x = (ventana.todayOffset + 0.5) * dayWidth - el.clientWidth * hoyEn;
    } else if (p?.tipo === 'centro') {
      x = ((p.ms - firstDayMs) / DIA_MS) * dayWidth - el.clientWidth / 2;
    } else if (bordeIzqMs.current != null) {
      x = ((bordeIzqMs.current - firstDayMs) / DIA_MS) * dayWidth;
    }
    if (x != null) el.scrollLeft = Math.max(0, x);
    bordeIzqMs.current = firstDayMs + (el.scrollLeft / dayWidth) * DIA_MS;
  }, [firstDayMs, dayWidth, ventana.todayOffset, viaje, scrollRef, lienzoRef, pintar, hoyEn]);

  useEffect(() => () => animacion.current?.stop(), []);

  // Scroll del usuario: recordar la fecha del borde izquierdo.
  const registrarScroll = useCallback((el) => {
    const a = actual.current;
    if (a.viaje) return;
    bordeIzqMs.current = a.firstDayMs + (el.scrollLeft / a.dayWidth) * DIA_MS;
  }, []);

  // Viaja a la escala `hasta`, dejando `centroDestinoMs` en el centro de la
  // pantalla (por defecto, la fecha que esta en el centro ahora).
  const irA = useCallback((hasta, centroDestinoMs = null) => {
    const el = scrollRef.current;
    const a = actual.current;
    // Si se interrumpe un viaje, el nuevo sale del ultimo cuadro pintado.
    const previo = a.viaje ? cuadroActual.current : null;
    animacion.current?.stop();
    animacion.current = null;
    if (!el || !lienzoRef.current) {
      cuadroActual.current = null;
      setViaje(null);
      setZoom(hasta);
      return;
    }

    const dw0 = previo ? previo.dw : a.dayWidth;
    const centro0 = previo
      ? previo.centroMs
      : a.firstDayMs + ((el.scrollLeft + el.clientWidth / 2) / dw0) * DIA_MS;
    const centro1 = centroDestinoMs ?? centro0;
    const dw1 = escalas[hasta].px;
    setZoom(hasta);

    if (reduce || (dw0 === dw1 && Math.abs(centro1 - centro0) < DIA_MS / 100)) {
      cuadroActual.current = null;
      pendiente.current = { tipo: 'centro', ms: centro1 };
      setViaje(null);
      return;
    }

    // La escala de salida es la que mas se veia (si se interrumpe a la mitad).
    const desde = a.viaje ? ((previo?.p ?? 0) < 0.5 ? a.viaje.desde : a.viaje.hasta) : a.zoom;
    const ventanaViaje = unirVentanas(a.ventana, ventanaPara(hasta));
    const inicioMs = ventanaViaje.firstDay.getTime();
    const ancho = el.clientWidth;
    const log0 = Math.log(dw0);
    const log1 = Math.log(dw1);
    const cuadro = (p) => ({
      p,
      dw: Math.exp(log0 + (log1 - log0) * p),
      centroMs: centro0 + (centro1 - centro0) * p,
    });

    cuadroActual.current = cuadro(0);
    setViaje({
      desde,
      hasta,
      ventana: ventanaViaje,
      rango: rangoDelViaje({ inicioMs, centro0, centro1, dw0, dw1, ancho }),
      dw0,
    });

    animacion.current = animate(0, 1, {
      ...CAMARA_T,
      onUpdate: (p) => {
        const c = cuadro(p);
        cuadroActual.current = c;
        const lienzo = lienzoRef.current;
        const sc = scrollRef.current;
        if (!lienzo || !sc) return;
        lienzo.style.setProperty('--dw', String(c.dw));
        lienzo.style.setProperty('--p', String(p));
        sc.scrollLeft = Math.max(0, ((c.centroMs - inicioMs) / DIA_MS) * c.dw - ancho / 2);
      },
      onComplete: () => {
        animacion.current = null;
        cuadroActual.current = null;
        pendiente.current = { tipo: 'centro', ms: centro1 };
        setViaje(null);
      },
    });
  }, [reduce, ventanaPara, scrollRef, lienzoRef, escalas]);

  const cambiarZoom = useCallback((nuevo) => {
    const a = actual.current;
    if (nuevo === a.zoom && !a.viaje) return;
    irA(nuevo);
  }, [irA]);

  // Hoy queda en la fraccion `hoyEn` del ancho (el centro, en el Gantt).
  const irAHoy = useCallback(() => {
    const el = scrollRef.current;
    const a = actual.current;
    const corrimiento = el ? ((el.clientWidth * (0.5 - hoyEn)) / a.dayWidth) * DIA_MS : 0;
    irA(a.zoom, startOfDay(new Date()).getTime() + DIA_MS / 2 + corrimiento);
  }, [irA, scrollRef, hoyEn]);

  // Capas de escala para encabezado y grilla: en el viaje, la que sale y la
  // que entra (se funden con --p); en reposo, una sola.
  const capas = useMemo(() => (
    viaje && viaje.desde !== viaje.hasta
      ? [{ zoom: viaje.desde, rol: 'sale' }, { zoom: viaje.hasta, rol: 'entra' }]
      : [{ zoom: viaje ? viaje.hasta : zoom, rol: 'fija' }]
  ), [viaje, zoom]);

  return {
    zoom,
    // Ancho del dia de la escala elegida (lo que usan arrastres, clicks y flechas).
    dayWidth,
    // Ancho del dia con el que salio el viaje: mientras dura, los textos de las
    // barras (adentro o afuera) se deciden con el.
    dayWidthTextos: viaje ? viaje.dw0 : dayWidth,
    ventana,
    capas,
    rango: viaje ? viaje.rango : null,
    viajando: !!viaje,
    cambiarZoom,
    irA,
    irAHoy,
    registrarScroll,
  };
};

export default useCamaraGantt;
